import { supabase } from '../lib/supabase';

/**
 * Lumo Strategy Service
 * Even-tone authenticity, micro-probes, neutral confidence coaching
 */

/**
 * Get Lumo coaching feedback for an objection stack
 * @param {string} objectionStackId - Objection stack UUID
 * @returns {Promise<Array>} Lumo-style coaching feedback
 */
export const getLumoCoachingFeedback = async (objectionStackId) => {
  try {
    const { data, error } = await supabase.rpc('get_lumo_coaching_feedback', {
      p_objection_stack_id: objectionStackId
    });

    if (error) throw error;

    return data.map(feedback => ({
      template: feedback.feedback_template,
      category: feedback.feedback_category,
      tone: feedback.feedback_tone,
      message: feedback.populated_feedback
    }));
  } catch (error) {
    console.error('Error getting Lumo coaching feedback:', error);
    throw error;
  }
};

/**
 * Calculate Lumo scores for objection handling performance
 * @param {Object} sessionData - Session performance data
 * @returns {Object} Lumo scores (tone, probe, detachment)
 */
export const calculateLumoScores = (sessionData) => {
  const {
    responseLength = 0,
    questionCount = 0,
    statementCount = 0,
    salesyPhraseCount = 0,
    pauseAfterObjection = 0,
    pivotSmoothness = 0,
    outcomeNeediness = 0
  } = sessionData;

  // Tone Consistency Score (0-1)
  // Factors: Avoids salesy phrases, maintains even pace, pauses appropriately
  const toneScore = Math.max(0, Math.min(1, 
    1.0 
    - (salesyPhraseCount * 0.2) // -0.2 per salesy phrase
    - (outcomeNeediness * 0.3)  // -0.3 for desperate tone
    + (pauseAfterObjection >= 1 ? 0.1 : 0) // +0.1 for thoughtful pause
  ));

  // Probe Sharpness Score (0-1)
  // Factors: More questions than statements, concise responses
  const probeScore = Math.max(0, Math.min(1,
    (questionCount / Math.max(questionCount + statementCount, 1)) * 0.7 // Question ratio (70%)
    + (responseLength < 50 ? 0.3 : responseLength < 100 ? 0.15 : 0) // Brevity bonus (30%)
  ));

  // Detachment Score (0-1)
  // Factors: Uses exit ramps, doesn't chase, maintains neutral confidence
  const detachmentScore = Math.max(0, Math.min(1,
    1.0
    - (outcomeNeediness * 0.4) // -0.4 for neediness signals
    + (pivotSmoothness * 0.3)  // +0.3 for calm pivots
  ));

  return {
    toneConsistency: parseFloat(toneScore.toFixed(2)),
    probeSharpness: parseFloat(probeScore.toFixed(2)),
    detachment: parseFloat(detachmentScore.toFixed(2)),
    overall: parseFloat(((toneScore + probeScore + detachmentScore) / 3).toFixed(2))
  };
};

/**
 * Track objection stack with Lumo scores
 * @param {Object} stackData - Objection stack data with Lumo metrics
 * @returns {Promise<Object>} Created stack with feedback
 */
export const trackObjectionStackWithLumo = async (stackData) => {
  const {
    userId,
    sessionId,
    sessionType,
    objectionSequence,
    successfulPivots = 0,
    failedPivots = 0,
    pivotPaths = [],
    overallHandlingScore,
    timeToResolve,
    resolutionOutcome,
    // Lumo-specific metrics
    lumoScores = {},
    previousSessionScore = null
  } = stackData;

  try {
    // Calculate improvement delta
    let improvementDelta = null;
    if (previousSessionScore !== null && overallHandlingScore !== null) {
      improvementDelta = ((overallHandlingScore - previousSessionScore) / previousSessionScore) * 100;
    }

    // Insert objection stack with Lumo scores
    const { data: stack, error } = await supabase
      .from('objection_stacks')
      .insert({
        user_id: userId,
        session_id: sessionId,
        session_type: sessionType,
        objection_sequence: objectionSequence,
        successful_pivots: successfulPivots,
        failed_pivots: failedPivots,
        pivot_paths: pivotPaths,
        overall_handling_score: overallHandlingScore,
        time_to_resolve: timeToResolve,
        resolution_outcome: resolutionOutcome,
        tone_consistency_score: lumoScores.toneConsistency,
        probe_sharpness_score: lumoScores.probeSharpness,
        detachment_score: lumoScores.detachment,
        improvement_delta: improvementDelta
      })
      .select()
      .single();

    if (error) throw error;

    // Get Lumo coaching feedback
    const feedback = await getLumoCoachingFeedback(stack.id);

    return {
      stack,
      feedback,
      lumoScores,
      improvementDelta
    };
  } catch (error) {
    console.error('Error tracking objection stack with Lumo:', error);
    throw error;
  }
};

/**
 * Get persona strategy with Lumo enhancements
 * @param {string} objectionId - Objection UUID
 * @param {string} personaType - Buyer persona
 * @returns {Promise<Object>} Persona strategy with Lumo tone guidance
 */
export const getPersonaStrategyWithLumo = async (objectionId, personaType) => {
  try {
    const { data, error } = await supabase
      .from('objection_persona_strategies')
      .select('*')
      .eq('objection_id', objectionId)
      .eq('persona_type', personaType)
      .single();

    if (error) throw error;

    return {
      personaType: data.persona_type,
      proofConstruct: data.proof_construct,
      evidencePriority: data.evidence_priority,
      languageStyle: data.language_style,
      acknowledge: data.acknowledge_variant,
      probe: data.probe_variant,
      value: data.value_variant,
      nextStep: data.next_step_variant,
      // Lumo Strategy fields
      lumoTone: data.lumo_tone || 'neutral_confident',
      avoidPhrases: data.avoid_phrases || [],
      frameFlipQuestion: data.frame_flip_question,
      maxHintLength: data.max_hint_length || 240
    };
  } catch (error) {
    console.error('Error fetching persona strategy with Lumo:', error);
    throw error;
  }
};

/**
 * Generate Lumo-style coaching note
 * @param {Object} performanceData - Performance metrics
 * @returns {string} Coaching note in Lumo voice
 */
export const generateLumoCoachingNote = (performanceData) => {
  const {
    lumoScores,
    improvementDelta,
    successfulPivots,
    failedPivots,
    timeToResolve,
    prevTimeToResolve
  } = performanceData;

  const notes = [];

  // Tone feedback
  if (lumoScores.toneConsistency >= 0.85) {
    notes.push('Your tone was rock solid — calm, no desperation. That\'s the move.');
  } else if (lumoScores.toneConsistency < 0.6) {
    notes.push('Tone got a bit eager there. Stay neutral — you don\'t need this deal.');
  }

  // Probe feedback
  if (lumoScores.probeSharpness >= 0.8) {
    notes.push('Sharp questions, no monologues. You\'re asking, not telling — exactly right.');
  } else if (lumoScores.probeSharpness < 0.6) {
    notes.push('Too much talking. Drop a single sharp question next time: "If X wasn\'t the issue, what would be?"');
  }

  // Detachment feedback
  if (lumoScores.detachment >= 0.85) {
    notes.push('Zero neediness detected. That detachment is magnetic — keep that energy.');
  } else if (lumoScores.detachment < 0.6) {
    notes.push('You\'re chasing too hard. Try: "Totally fine if this isn\'t a fit" — detach from the outcome.');
  }

  // Stacking feedback
  if (successfulPivots > 0 && failedPivots === 0) {
    notes.push(`You navigated ${successfulPivots} objection${successfulPivots > 1 ? 's' : ''} without losing the thread. That's stacking done right.`);
  } else if (failedPivots > successfulPivots) {
    notes.push('You lost the thread when objections stacked. Anchor with a micro-probe before pivoting.');
  }

  // Better than yesterday
  if (improvementDelta !== null) {
    if (improvementDelta > 5) {
      notes.push(`You improved ${improvementDelta.toFixed(1)}% from last session. That's the better-than-yesterday loop.`);
    } else if (improvementDelta < -5) {
      notes.push(`Score dipped ${Math.abs(improvementDelta).toFixed(1)}% from last time. Let's tighten the probes — you had this before.`);
    }
  }

  // Speed feedback
  if (prevTimeToResolve && timeToResolve < prevTimeToResolve) {
    const speedup = prevTimeToResolve - timeToResolve;
    notes.push(`You handled that ${speedup}s faster than yesterday. Speed + composure = winning.`);
  }

  return notes.join(' ');
};

/**
 * Get avoid phrases for persona (salesy jargon to eliminate)
 * @param {string} personaType - Buyer persona
 * @returns {Array<string>} Phrases to avoid
 */
export const getAvoidPhrases = (personaType) => {
  const commonAvoid = [
    'I totally understand',
    'I hear you',
    'Great question',
    'Let me circle back',
    'To be honest with you',
    'I promise you',
    'Trust me',
    'At the end of the day'
  ];

  const personaSpecific = {
    CFO: ['This is a no-brainer', 'You\'d be crazy not to', 'Everyone is doing this'],
    CEO: ['Let me walk you through', 'This will transform your business', 'Game changer'],
    SMB_OWNER: ['This is enterprise-grade', 'Best-in-class solution', 'Industry-leading'],
    TECH_LEAD: ['It just works', 'Plug and play', 'Cutting-edge technology']
  };

  return [...commonAvoid, ...(personaSpecific[personaType] || [])];
};

export default {
  getLumoCoachingFeedback,
  calculateLumoScores,
  trackObjectionStackWithLumo,
  getPersonaStrategyWithLumo,
  generateLumoCoachingNote,
  getAvoidPhrases
};
